/* cl-mcarousel.js — dots, auto-rotation and snap correction for the MOBILE
   swipe carousels on the homepage (the USP band and the "Sell with us" steps).

   The carousel itself is pure CSS: under 1024px the strip becomes a
   scroll-snap container, so swiping, momentum and the snap all come from the
   browser. This file adds the page indicator, which CSS cannot express, plus
   two things layered on top of it — see AUTOPLAY and SETTLE below.

   DOTS ARE BUILT HERE, not in Liquid, because the item count is not knowable
   from the block count: the USP bar drops any selling tile outside Singapore
   (snippets/cl-sell-allowed.liquid), so a server-rendered dot per block would
   over-count in every other market. Building from the DOM counts what is
   actually on the page. The trade-off is that with JS off there are no dots —
   acceptable, because the strip still swipes and every item is reachable.

   Markup contract:
     <div class="cl-mdots" data-cl-dots="#scroller-id" data-cl-item=".item"
          data-cl-autoplay="1" data-cl-interval="4000"></div>
   `data-cl-item` is optional; without it every element child counts.
   `data-cl-autoplay` / `data-cl-interval` are optional and OPT-IN: a strip
   without them behaves exactly as it did before autoplay existed.

   POSITIONS ARE MEASURED FROM LIVE RECTS AND EXPRESSED AS CENTRES — never
   i * clientWidth, and never a left edge. See the Geometry block below for why
   both of those leave the copy off-centre on every slide but the first.

   Above 1023px the scroller is a normal flex row with nothing to scroll, so
   every measurement collapses to 0, autoplay's scrollable() guard fails and
   this stays inert — the dots are display:none there. */
(function () {
  /* Below this, a strip is not scrollable and there is nothing to page. Two
     pixels of slack absorbs sub-pixel layout noise on fractional viewports. */
  var SCROLL_EPS = 2;
  /* A position more than this far from the nearest slide's own offset is a
     stalled snap, not a rounding artefact — see SETTLE. */
  var DRIFT_EPS = 1;
  /* Quiet time after the last scroll event before a position counts as final.
     Long enough that momentum has died on iOS, short enough that a stalled
     slide is corrected before it can be read as the resting state. */
  var IDLE_MS = 140;

  function prefersReduced() {
    try {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (e) {
      return false;
    }
  }

  function init(dots) {
    /* Per-element, not per-window: the theme editor re-renders one section at a
       time and re-runs the scripts inside it, and a window-level guard would
       leave the fresh markup dotless. */
    if (dots.getAttribute('data-cl-dots-ready') === '1') return;

    var scroller = document.querySelector(dots.getAttribute('data-cl-dots') || '');
    if (!scroller) return;

    var sel = dots.getAttribute('data-cl-item');
    var items = sel ? scroller.querySelectorAll(sel) : scroller.children;
    var n = items.length;
    /* One item cannot be paged. Leaving the container empty keeps the layout
       identical to a bar that never had dots. */
    if (n < 2) return;

    dots.setAttribute('data-cl-dots-ready', '1');

    var interval = parseInt(dots.getAttribute('data-cl-interval'), 10);
    if (!isFinite(interval) || interval < 1500) interval = 4000;
    /* Reduced motion opts OUT of the rotation entirely rather than merely
       slowing it — an auto-advancing band is exactly the kind of unrequested
       movement the setting exists to stop. Swipe and dots still work, so no
       promise becomes unreachable. Same call as cl-hero-slider.js. */
    var autoplay = dots.getAttribute('data-cl-autoplay') === '1' && !prefersReduced();

    for (var i = 0; i < n; i++) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'cl-mdot' + (i === 0 ? ' is-active' : '');
      b.setAttribute('data-cl-i', i);
      b.setAttribute('aria-label', 'Item ' + (i + 1) + ' of ' + n);
      if (i === 0) b.setAttribute('aria-current', 'true');
      dots.appendChild(b);
    }
    var btns = dots.querySelectorAll('.cl-mdot');

    /* ── Geometry ───────────────────────────────────────────────────────────
       EVERYTHING HERE IS MEASURED FROM LIVE RECTS AND EXPRESSED IN CENTRES.

       Not i * clientWidth, which assumes a slide is exactly as wide as the
       strip: clientWidth is a rounded integer, a 100%-wide slide in a
       fractional viewport is not, and any difference between the two leaves the
       copy off-centre by half of it on every slide except the first (scrollLeft
       0 has nothing to get wrong). That is the "only the first one is centred"
       report.

       Not offsetLeft either, which is measured from the nearest positioned
       ancestor and so silently assumes the first item sits at the content
       origin — untrue the moment the strip gains padding or a leading sibling
       such as the optional USP heading.

       A rect delta plus the CURRENT scrollLeft reconstructs the absolute
       content position with no assumption about any of that, and asking for
       centres rather than edges means the copy is centred whatever the slide
       measures. Matches scroll-snap-align: center in cl-home.css. */
    function box() {
      return scroller.getBoundingClientRect();
    }

    /** Scroll position that puts slide k's centre on the strip's centre. */
    function targetOf(k, rect) {
      var s = rect || box();
      var r = items[k].getBoundingClientRect();
      var centre = r.left - s.left + scroller.scrollLeft + r.width / 2;
      var want = centre - s.width / 2;
      var max = scroller.scrollWidth - scroller.clientWidth;
      /* The first and last slides cannot be centred any further than the ends
         of the track; clamping is what keeps their dot reachable. */
      if (want < 0) want = 0;
      if (want > max) want = max;
      return want;
    }

    function scrollable() {
      return scroller.scrollWidth - scroller.clientWidth > SCROLL_EPS;
    }

    /** Index of the slide whose centre is closest to the strip's centre. */
    function nearest() {
      var s = box();
      var mid = s.left + s.width / 2;
      var best = 0;
      var bestD = Infinity;
      for (var k = 0; k < n; k++) {
        var r = items[k].getBoundingClientRect();
        var d = Math.abs(r.left + r.width / 2 - mid);
        if (d < bestD) {
          bestD = d;
          best = k;
        }
      }
      return best;
    }

    var index = 0;

    function paint() {
      index = nearest();
      for (var k = 0; k < n; k++) {
        var on = k === index;
        btns[k].classList.toggle('is-active', on);
        if (on) btns[k].setAttribute('aria-current', 'true');
        else btns[k].removeAttribute('aria-current');
      }
    }

    function go(k, smooth) {
      /* scrollTo, not scrollIntoView: the latter also scrolls the PAGE to bring
         the strip into view, which yanks the viewport when a dot is tapped
         while the band is only half on screen. */
      scroller.scrollTo({ left: targetOf(k), behavior: smooth ? 'smooth' : 'auto' });
    }

    /* ── SETTLE ─────────────────────────────────────────────────────────────
       scroll-snap-type is mandatory, but a snap can still be left unfinished:
       an interrupted flick, a smooth programmatic scroll cut short, or a
       viewport that changed width mid-gesture. What that looks like on the USP
       band is an icon and two lines of copy sitting a few px off centre with
       a sliver of the neighbouring promise showing — the reported bug.

       So once scrolling has been quiet for IDLE_MS and no finger is down, the
       position is compared against the centring target for the nearest slide
       and corrected if it is off. 'auto', not 'smooth': this is a correction of
       something that should already have happened, and animating it would read
       as the carousel twitching on its own. */
    var pointers = 0;
    var idle = null;

    function settle() {
      idle = null;
      if (pointers > 0 || !scrollable()) return;
      var target = targetOf(nearest());
      if (Math.abs(scroller.scrollLeft - target) > DRIFT_EPS) {
        scroller.scrollTo({ left: target, behavior: 'auto' });
      }
    }

    var frame = null;
    scroller.addEventListener(
      'scroll',
      function () {
        if (idle) clearTimeout(idle);
        idle = setTimeout(settle, IDLE_MS);
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          paint();
        });
      },
      { passive: true },
    );

    /* ── AUTOPLAY ───────────────────────────────────────────────────────────
       Stopped for good on any deliberate navigation (swipe, dot). That is the
       "mechanism to pause" WCAG 2.2.2 asks for, and it matches what people
       expect: once you have taken hold of a carousel it should not yank itself
       out from under you a few seconds later.

       It also only runs while the band is actually on screen. A strip that
       rotated unseen would be halfway through its cycle by the time it is
       scrolled to, so the first promise — the one the copy is ordered around —
       would rarely be the one anybody reads. */
    var timer = null;
    var visible = true;

    function tick() {
      /* The theme editor replaces a section's DOM wholesale, which leaves this
         closure holding a detached scroller and its interval still ticking. */
      if (scroller.isConnected === false) {
        stop();
        return;
      }
      /* Re-checked every tick rather than once at init: a rotate to landscape
         or a resize past 1023px turns the strip back into a plain flex row with
         nothing to scroll, and advancing it there would move the desktop bar. */
      if (!visible || document.hidden || pointers > 0 || !scrollable()) return;
      go((nearest() + 1) % n, true);
    }

    function start() {
      if (!autoplay || timer) return;
      timer = setInterval(tick, interval);
    }

    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    function surrender() {
      autoplay = false;
      stop();
    }

    dots.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('.cl-mdot') : null;
      if (!b) return;
      surrender();
      go(parseInt(b.getAttribute('data-cl-i'), 10) || 0, true);
    });

    /* Pointer bookkeeping serves three jobs at once: it pauses the rotation for
       the duration of a touch, keeps settle() from fighting a finger, and
       decides whether the strip has been taken over.

       That last one is measured, NOT assumed from the touch: most fingers that
       land on this band are scrolling the PAGE past it, and surrendering on
       pointerdown alone would mean the rotation dies the first time anyone
       scrolls the homepage with their thumb over the USP strip. Only a gesture
       that actually moved the strip sideways counts as taking hold of it.
       pointercancel/lostpointercapture are included because a gesture that
       becomes a page scroll never fires pointerup. */
    var downAt = 0;
    scroller.addEventListener('pointerdown', function () {
      pointers++;
      downAt = scroller.scrollLeft;
    });
    function release() {
      if (pointers > 0) pointers--;
      if (pointers === 0 && Math.abs(scroller.scrollLeft - downAt) > 4) surrender();
    }
    scroller.addEventListener('pointerup', release);
    scroller.addEventListener('pointercancel', release);
    scroller.addEventListener('lostpointercapture', release);

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    if (autoplay && 'IntersectionObserver' in window) {
      /* Half the band showing is the threshold: the dots sit below the strip,
         so a band that is only peeking in from the bottom edge is not yet
         something anyone is reading. */
      new IntersectionObserver(
        function (entries) {
          visible = entries[entries.length - 1].isIntersecting;
        },
        { threshold: 0.5 },
      ).observe(scroller);
    }

    /* A rotate to landscape changes every item offset, so the index derived
       from them — and the resting position itself — move under the same
       scrollLeft. Re-align to the slide that was showing rather than merely
       repainting the dots. */
    window.addEventListener('resize', function () {
      paint();
      if (idle) clearTimeout(idle);
      idle = setTimeout(settle, IDLE_MS);
    });

    paint();
    start();
  }

  function boot(root) {
    var list = (root || document).querySelectorAll('[data-cl-dots]');
    for (var i = 0; i < list.length; i++) init(list[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
